import { Imports, OHIF, TaskAdmin } from '@gleamer/types';
import { responseDetail } from './errors';

type AuthHeaderSource = Pick<
  OHIF.UserAuthenticationService,
  'getAuthorizationHeader'
>;

type AdminLabelTask = TaskAdmin.AdminLabelTask;
type AdminLabelTaskDetails = TaskAdmin.AdminLabelTaskDetails;
type Page<T> = Imports.Page<T>;
type TaskComputation = TaskAdmin.TaskComputation;
type TaskComputationIssues = TaskAdmin.TaskComputationIssues;
type PredictionImport = TaskAdmin.PredictionImport;
type PredictionImportAction = TaskAdmin.PredictionImportAction;

/**
 * As many tasks as the backoffice reads in one request: all of them (production has about 2,000), so the
 * page searches, filters, sorts and groups in the browser — the list endpoint does none of it. 2,000 is
 * also Spring Data's largest page.
 */
export const ADMIN_TASKS_LIMIT = 2000;

async function getJson<T>(auth: AuthHeaderSource, url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    headers: new Headers(auth.getAuthorizationHeader()),
  });
  if (!response.ok) {
    throw new Error(`${response.status} : ${response.statusText}`);
  }
  return response.json();
}

/** Every task, disabled ones included, newest first (GET /api/label-tasks, list:labelTask). */
export function fetchAdminTasks(
  auth: AuthHeaderSource,
  size = ADMIN_TASKS_LIMIT
): Promise<Page<AdminLabelTask>> {
  return getJson(
    auth,
    `/api/label-tasks?page=0&size=${size}&sort=createdAt,desc`
  );
}

/** An answer of viewer-api other than 2xx, with what it said about it when it said something (a 409's reason). */
export class TaskAdminApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'TaskAdminApiError';
  }
}

/** The error an answer other than 2xx makes, with viewer-api's reason when it gave one ({@link responseDetail}). */
export async function failure(response: Response): Promise<TaskAdminApiError> {
  const detail = await responseDetail(response);
  return new TaskAdminApiError(
    response.status,
    detail
      ? `${response.status}: ${detail}`
      : `${response.status} : ${response.statusText}`
  );
}

async function postJson<T>(auth: AuthHeaderSource, url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: new Headers(auth.getAuthorizationHeader()),
  });
  if (!response.ok) {
    throw await failure(response);
  }
  return response.json();
}

const id = (value: number | string) => encodeURIComponent(String(value));

/** The computations of a task's items, newest first (GET /api/label-tasks/{taskId}/computations, read:labelTask). */
export function fetchTaskComputations(
  auth: AuthHeaderSource,
  taskId: number,
  limit = 10
): Promise<TaskComputation[]> {
  return getJson(
    auth,
    `/api/label-tasks/${id(taskId)}/computations?limit=${limit}`
  );
}

/** A page of one computation's issues, with their total (GET /api/task-computations/{id}/issues). */
export function fetchComputationIssues(
  auth: AuthHeaderSource,
  computationId: number,
  { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}
): Promise<TaskComputationIssues> {
  return getJson(
    auth,
    `/api/task-computations/${id(computationId)}/issues?limit=${limit}&offset=${offset}`
  );
}

/** A task's prediction imports, newest first, without their issues (GET /api/label-tasks/{taskId}/prediction-imports). */
export function fetchTaskPredictionImports(
  auth: AuthHeaderSource,
  taskId: number
): Promise<PredictionImport[]> {
  return getJson(auth, `/api/label-tasks/${id(taskId)}/prediction-imports`);
}

/** One import, with a page of its issues and their count (GET /api/prediction-imports/{id}). */
export function fetchPredictionImport(
  auth: AuthHeaderSource,
  importId: number,
  { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}
): Promise<PredictionImport> {
  return getJson(
    auth,
    `/api/prediction-imports/${id(importId)}?limit=${limit}&offset=${offset}`
  );
}

/** Imports the task's prediction table again (POST, create:labelTask); 409 while one of its imports is in flight. */
export function importPredictionsAgain(
  auth: AuthHeaderSource,
  taskId: number
): Promise<PredictionImport> {
  return postJson(auth, `/api/label-tasks/${id(taskId)}/prediction-imports`);
}

/**
 * Stops a computation that runs and will not finish (update:labelTask): FAILED, "STOPPED by <who>", and its task
 * released to its labellers. 409 when it is not running any more.
 */
export function stopTaskComputation(
  auth: AuthHeaderSource,
  computationId: number
): Promise<TaskComputation> {
  return postJson(auth, `/api/task-computations/${id(computationId)}/stop`);
}

/**
 * Settles an import (create:prediction): replays it (PARTIAL_FAILURE or FAILED), accepts it as it is
 * (PARTIAL_FAILURE → DONE) or abandons it (→ FAILED). 409 when its status does not allow it.
 */
export function settlePredictionImport(
  auth: AuthHeaderSource,
  importId: number,
  action: PredictionImportAction
): Promise<PredictionImport> {
  return postJson(auth, `/api/prediction-imports/${id(importId)}/${action}`);
}

/** One task's last revision, with its taskers (GET /api/label-tasks/{taskId}/details, read:labelTask). */
export function fetchAdminTaskDetails(
  auth: AuthHeaderSource,
  taskId: number
): Promise<AdminLabelTaskDetails> {
  return getJson(
    auth,
    `/api/label-tasks/${encodeURIComponent(String(taskId))}/details`
  );
}

/** A task viewer-api refused: its reason, and the fields at fault when it validated them. */
export class TaskCreationError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly violations: { field: string; message: string }[] = []
  ) {
    super(message);
    this.name = 'TaskCreationError';
  }
}

/**
 * Creates a task from its YAML document (POST /api/label-tasks, create:labelTask, multipart `labelTask`) —
 * the file the data managers upload today. Answers the task as created, with its id.
 */
export async function createAdminTask(
  auth: AuthHeaderSource,
  yaml: string,
  fileName: string
): Promise<AdminLabelTaskDetails> {
  const form = new FormData();
  form.append(
    'labelTask',
    new Blob([yaml], { type: 'application/x-yaml' }),
    fileName
  );
  const response = await fetch('/api/label-tasks', {
    method: 'POST',
    headers: new Headers(auth.getAuthorizationHeader()),
    body: form,
  });
  if (!response.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = await response.json();
    } catch {
      // no JSON body
    }
    const violations = Array.isArray(body.violations)
      ? (body.violations as { field: string; message: string }[])
      : [];
    const detail =
      (body.detail as string) ??
      (body.message as string) ??
      (violations.length
        ? violations.map(v => `${v.field}: ${v.message}`).join('; ')
        : response.statusText);
    throw new TaskCreationError(
      response.status,
      `${response.status}: ${detail}`,
      violations
    );
  }
  return response.json();
}
